/* eslint-disable max-lines */
import _ from 'lodash';
import * as React from 'react';
import { isPendingPromiseObservable } from './promises';
import { GenericFunction, Option } from './utilityTypes';
import { LazyObservablePromise } from './LazyObservablePromise';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { generatePath } from './router';
import { scrollToTop } from './transitions';
import { renderUrl } from '.';
import { setMetaDescription, setTitle } from './dom';

type Observable<T = unknown> = LazyObservablePromise<T>;
export type SetStateLoading = <T>(R: Observable<T>) => Observable<T>;
export type SetStateLoadingMultiple = <T>(
    R: Observable<T>,
    slugOrIndex: string | number
) => Observable<T>;

export function useLoadingState(): [
    Option<Observable>,
    SetStateLoading,
    () => boolean
] {
    const [state, setState] = React.useState<Observable>();
    return [
        state,
        request => {
            setState(request);
            return request;
        },
        () => isLoadingState(state),
    ];
}

export function useLoadingStates(): [
    (slugOrIndex: string | number) => { loading: boolean; disabled: boolean },
    SetStateLoadingMultiple
] {
    const [state, setState] = React.useState<
        Record<string | number, Observable>
    >({});
    return [
        slugOrIndex => {
            const request = state[slugOrIndex];
            const loading = request && isLoadingState(request);
            return {
                loading,
                disabled:
                    !loading &&
                    Object.entries(state).some(
                        ([k, v]) => k !== slugOrIndex && isLoadingState(v)
                    ),
            };
        },
        (request, slugOrIndex) => {
            setState({ ...state, [slugOrIndex]: request });
            return request;
        },
    ];
}

export type ChoiceLoadingState<Choice, Response> = {
    observable: LazyObservablePromise<Response>;
    choice: Choice;
};

/** Use this as the React.setState type when there is only one request type but
 * the arguments matter to define which option is going to be shown as loading
 * or blocking.
 */
export function useChoiceLoadingState<Choice = string, Response = unknown>(): [
    getChoice: () => Choice | undefined,
    setChoice: (
        choice: Choice,
        observable: LazyObservablePromise<Response>
    ) => void,
    rawState: ChoiceLoadingState<Choice, Response> | undefined
] {
    const [state, setState] =
        React.useState<ChoiceLoadingState<Choice, Response>>();

    return [
        () =>
            state && isLoadingState(state.observable)
                ? state.choice
                : undefined,
        (choice, observable) =>
            setState({
                choice,
                observable,
            }),
        state,
    ];
}

export function isLoadingState(value?: Observable): boolean {
    return isPendingPromiseObservable(value);
}

export type ConditionalSetStateHook<T> = [
    state: T,
    conditionalSetState: (v: T) => void,
    forceState: (v: T) => void
];

export function useConditionalSetState<T>(
    initial: T,
    conditionToSet: (value: T) => boolean
): ConditionalSetStateHook<T> {
    const [state, setState] = React.useState(initial);
    const conditionalSetState = (value: T) => {
        if (conditionToSet(value)) {
            setState(value);
        }
    };
    return [state, conditionalSetState, setState];
}

export function useConditionallyDebouncedSetState<T>(
    initial: T,
    conditionToSet: (value: T) => boolean,
    conditionToDebounce: (value: T) => boolean,
    delayInMillisecs = 750
): ConditionalSetStateHook<T> {
    const [state, conditionalSetState, forceState] = useConditionalSetState<T>(
        initial,
        conditionToSet
    );
    const debouncedSetState = useConditionallyDebouncedFn(
        conditionalSetState,
        conditionToDebounce,
        delayInMillisecs
    );
    return [state, debouncedSetState, forceState];
}

export function useToggler(initial = false): [boolean, () => void] {
    const [isVisible, setIsVisible] = React.useState(initial);
    return [isVisible, () => setIsVisible(!isVisible)];
}

export function useVisibility(
    initial = false
): [visible: boolean, show: () => void, hide: () => void] {
    const [isVisible, setIsVisible] = React.useState(initial);
    return [isVisible, () => setIsVisible(true), () => setIsVisible(false)];
}

export function useGeolocation({
    options,
    disabled,
}: {
    options?: PositionOptions;
    disabled?: boolean;
} = {}): [loading: boolean, data: GeolocationCoordinates | null] {
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState<GeolocationCoordinates | null>(null);

    const locationOptions = options ?? {
        enableHighAccuracy: true,
        maximumAge: 0,
    };

    React.useEffect(() => {
        if (disabled) {
            setLoading(false);
            return;
        }

        const successHandler: PositionCallback = e => {
            setLoading(false);
            setData(e.coords);
        };
        const errorHandler: PositionErrorCallback = _ => {
            setLoading(false);
        };
        navigator.geolocation.getCurrentPosition(
            successHandler,
            errorHandler,
            locationOptions
        );
        const id = navigator.geolocation.watchPosition(
            successHandler,
            errorHandler,
            locationOptions
        );
        return () => navigator.geolocation.clearWatch(id);
    }, [options]);
    return [loading, data];
}

export type DetailsDrawerState<ID> = {
    id?: ID;
    visible: boolean;
    title?: string;
};

export type ListVisibility<ID = number> = [
    (id: ID) => boolean,
    (id: ID) => void,
    (id: ID) => void
];

export function useListVisibility<ID = number>(
    initial: ID[] = []
): ListVisibility<ID> {
    const [visible, setVisible] = React.useState<Set<ID>>(new Set(initial));

    return [
        id => visible.has(id),
        id => setVisible(new Set(visible).add(id)),
        id => {
            visible.delete(id);
            setVisible(new Set(visible));
        },
    ];
}

export function useListToggler<ID = number>(
    initial: ID[] = []
): [ID[], (id: ID) => void, (items: ID[]) => void] {
    const [visibleItems, setVisibleItems] = React.useState(initial);
    const toggleItemVisibility = (id: ID) => {
        setVisibleItems(
            visibleItems.includes(id)
                ? visibleItems.filter(x => x !== id)
                : visibleItems.concat(id)
        );
    };
    return [visibleItems, toggleItemVisibility, setVisibleItems];
}

export type Redirect = (
    path: string,
    params?: Record<string, string | number | boolean> | undefined,
    scroll?: boolean,
    queryParams?: Record<string, string | number | boolean> | undefined
) => void;

export function useRedirect(
    scrollToTopAfter = true,
    scrollBehavior: ScrollBehavior | undefined = undefined
): Redirect {
    const history = useHistory();
    return (path, params, scroll = scrollToTopAfter, queryParams = {}) => {
        const from = history.location.pathname;
        history.push(
            renderUrl(generatePath(path, { ...params }), {}, queryParams),
            {
                from,
            }
        );
        // deferring the call to allow the page to be filled after the route
        // changes
        scroll && setTimeout(() => scrollToTop(scrollBehavior));
    };
}

// See: https://www.benmvp.com/blog/copy-to-clipboard-react-custom-hook/
export type ClipboardCopyStatus = 'idle' | 'copied' | 'failed';

export const useCopyToClipboard = (
    text: string,
    notifyTimeout = 1200
): [ClipboardCopyStatus, VoidFunction] => {
    const [copyStatus, setCopyStatus] =
        React.useState<ClipboardCopyStatus>('idle');

    const copy = React.useCallback(() => {
        navigator.clipboard.writeText(text).then(
            () => setCopyStatus('copied'),
            () => setCopyStatus('failed')
        );
    }, [text]);

    React.useEffect(() => {
        if (copyStatus === 'idle') {
            return;
        }

        const timeoutId = setTimeout(
            () => setCopyStatus('idle'),
            notifyTimeout
        );

        return () => clearTimeout(timeoutId);
    }, [copyStatus]);

    return [copyStatus, copy];
};

export const useStateMap = <K, V>(
    initial: Map<K, V>,
    keyFn: (v: V) => K
): [
    Map<K, V>,
    {
        setValue: (k: K, v: V) => V[];
        deleteValue: (k: K) => V[];
        clear: () => void;
        getValue: (k: K) => V | undefined;
        putValue: (v: V) => V[];
        array: V[];
        replace: (map: Map<K, V>) => void;
    }
] => {
    const [map, setMap] = React.useState(new Map<K, V>(initial ?? []));
    const setValue = (key: K, val: V) => {
        setMap(new Map(map.set(key, val)));
        return array.filter(v => keyFn(v) !== key).concat(val);
    };
    const putValue = (val: V) => {
        return setValue(keyFn(val), val);
    };
    const array = Array.from(map, ([, data]) => data);
    const getValue = map.get.bind(map);
    const deleteValue = (key: K) => {
        map.delete(key);
        setMap(new Map(map));
        return array.filter(v => keyFn(v) !== key);
    };
    const clear = () => {
        map.clear();
        setMap(map);
    };
    return [
        map,
        {
            setValue,
            getValue,
            deleteValue,
            clear,
            putValue,
            replace: setMap,
            get array() {
                return array;
            },
        },
    ];
};

export const useUniqueId = (prefix?: string): string => {
    const [id] = React.useState(_.uniqueId(prefix));
    return id;
};

type PollFn<T> = () => PromiseLike<T>;

export const usePoll = <T>(
    fn: PollFn<T>,
    pollCondition: boolean,
    msTime = 10000,
    runNow = false
): void => {
    const [count, setCount] = React.useState(0);
    let token: NodeJS.Timeout | undefined = undefined;

    React.useEffect(() => {
        if (runNow) fn();
    }, []);

    React.useEffect(() => {
        let unmounted = false;
        if (!pollCondition) return;
        token && clearTimeout(token);
        token = setTimeout(async () => {
            await fn();
            if (!unmounted) setCount(count + 1);
        }, msTime);
        return () => {
            token && clearTimeout(token);
            unmounted = true;
        };
    }, [count, pollCondition]);
};

export const useTimer = (
    onInterval: VoidFunction,
    onTimeout: VoidFunction,
    condition: boolean,
    timeout: number,
    interval: number = 1000
): void => {
    const [elapsed, setElapsed] = React.useState(0);

    const tick = () => {
        const newElapsed = elapsed + interval;
        setElapsed(newElapsed);

        if (newElapsed >= timeout) {
            setElapsed(0);
            onTimeout();
        } else {
            onInterval();
        }

        return Promise.resolve();
    };

    usePoll(tick, condition && elapsed < timeout, interval);
};

export const useDebouncedFn = (
    fn: GenericFunction,
    delayInMillisecs = 750
): GenericFunction => {
    return React.useCallback(_.debounce(fn, delayInMillisecs), []);
};

export function useConditionallyDebouncedFn<T>(
    fn: (value: T) => void,
    conditionToDebounce: (value: T) => boolean,
    delayInMillisecs = 750
): (value: T) => void {
    const debounced = React.useCallback(_.debounce(fn, delayInMillisecs), []);
    return (value: T) => {
        if (conditionToDebounce(value)) {
            debounced(value);
        } else {
            debounced.cancel();
            fn(value);
        }
    };
}

export function useFirstRender(): boolean {
    const firstRender = React.useRef(true);

    React.useEffect(() => {
        firstRender.current = false;
    }, []);

    return firstRender.current;
}

export function useMediaQuery(query: string): boolean | undefined {
    const [matches, setMatches] = React.useState<boolean>();

    React.useEffect(() => {
        const mediaQuery = window.matchMedia(query);
        setMatches(mediaQuery.matches);
        const listener = (event: MediaQueryListEvent) =>
            setMatches(event.matches);
        mediaQuery.addEventListener('change', listener);
        return () => mediaQuery.removeEventListener('change', listener);
    }, []);

    return matches;
}

export type Device = 'desktop' | 'tablet' | 'mobile';

export function useDeviceQuery(): Device {
    const inTablet = useMediaQuery('all and (max-width: 1079.5px)');
    const inMobile = useMediaQuery('all and (max-width: 599.5px)');

    if (inMobile) return 'mobile';
    if (inTablet) return 'tablet';
    return 'desktop';
}

export function useVisibilityRouting<
    T extends { [K in keyof T]?: string | undefined }
>(
    generalRoute: string,
    ...detailedRoute: string[]
): [(visible: boolean, alwaysRedirect?: boolean) => void, T | undefined] {
    const history = useHistory<{ from?: string } | undefined>();
    const match = useRouteMatch<T>(detailedRoute);
    const redirect = useRedirect();
    return [
        (visible: boolean, alwaysRedirect = false, index = 0) => {
            if (!visible) {
                if (alwaysRedirect) {
                    history.replace(generalRoute);
                } else {
                    history.location.state?.from === generalRoute
                        ? history.goBack()
                        : history.replace(generalRoute);
                }
            } else {
                redirect(detailedRoute[index]);
            }
        },
        match?.params,
    ];
}

export function useForwardedState<T>(): [T | undefined, (a: T) => T] {
    const [chosen, setChosen] = React.useState<T>();
    const forwardChosen = (selected: T) => {
        setChosen(selected);
        return selected;
    };
    return [chosen, forwardChosen];
}

// Helper to set a callback function that is triggered right after the
// setState actually changes the state value.
export function useStateCallback<T>(
    initialState: T
): [T, (state: T, cb?: (state: T) => void) => void] {
    const [state, setState] = React.useState(initialState);
    const cbRef = React.useRef<((state: T) => void) | undefined>(undefined);

    const setStateCallback = React.useCallback(
        (state: T, cb?: (state: T) => void) => {
            cbRef.current = cb;
            setState(state);
        },
        []
    );

    React.useEffect(() => {
        if (cbRef.current) {
            cbRef.current(state);
            cbRef.current = undefined;
        }
    }, [state]);

    return [state, setStateCallback];
}

export function useSEO(title: string, description: string) {
    React.useEffect(() => {
        const originalDescription = setMetaDescription(description);
        const originalTitle = setTitle(title);
        return () => {
            setTitle(originalTitle);
            if (originalDescription) {
                setMetaDescription(originalDescription);
            }
        };
    }, []);
}

export default function useWindowDimensions(listenToResize: boolean) {
    const [windowDimensions, setWindowDimensions] = React.useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    React.useEffect(() => {
        function handleResize() {
            setWindowDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        }
        if (listenToResize) {
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }
    }, []);

    return windowDimensions;
}
